import { useTranslation } from 'react-i18next';
import ReportingDashboardSection, {
    SectionItem,
    SectionProps,
} from './ReportingDashboardSection';
import { GridItemSpans } from '../../../../../controls/ResponsiveGrid/ResponsiveGridItem';
import { useSelector } from '../../../../../store/hooks';
import RateTile from '../tiles/RateTile/RateTile';
import { QuestionCircleRegular } from '@fluentui/react-icons';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import { CheckInRates } from '../../../../../model/ReportingMetrics';

const useCheckInTileStyles = makeStyles({
    tileTitle: {
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
    },
    tooltipIconWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
});

const itemSpans: GridItemSpans = {
    tabletSpan: 6,
    desktopSpan: 4,
} as const;

const TileTitle: React.FC<{
    description: string;
    children: React.ReactNode;
}> = ({ description, children }) => {
    const styles = useCheckInTileStyles();

    return (
        <span className={styles.tileTitle}>
            {children}
            <Tooltip relationship="description" content={description}>
                <span className={styles.tooltipIconWrapper}>
                    <QuestionCircleRegular />
                </span>
            </Tooltip>
        </span>
    );
};

interface Tile {
    key: string;
    valueKey: keyof CheckInRates;
}

const tiles: readonly Tile[] = [
    { key: 'UniqueUsers', valueKey: 'uniqueUsers' },
    { key: 'AllUsers', valueKey: 'allUsers' },
    { key: 'FlightTeams', valueKey: 'flightTeams' },
];

const CheckInSection: React.FC<SectionProps> = ({ className }) => {
    const { t } = useTranslation();

    const { previousRange, currentRange } = useSelector(
        (x) => x.reportingDashboard
    );

    return previousRange && currentRange ? (
        <ReportingDashboardSection className={className}>
            <>
                {tiles.map(({ key, valueKey }) => {
                    const baseTranslationKey = `ReportingDashboard_CheckInRate_${key}`;

                    return (
                        <SectionItem key={key} {...itemSpans}>
                            <RateTile
                                title={
                                    <TileTitle
                                        description={t(
                                            baseTranslationKey + '_Tooltip'
                                        )}
                                    >
                                        {t(baseTranslationKey)}
                                    </TileTitle>
                                }
                                previousValue={
                                    previousRange.checkInRates[valueKey]
                                }
                                currentValue={
                                    currentRange.checkInRates[valueKey]
                                }
                                countTooltip={t(
                                    baseTranslationKey + '_CountTooltip'
                                )}
                                totalCountTooltip={t(
                                    baseTranslationKey + '_TotalCountTooltip'
                                )}
                            />
                        </SectionItem>
                    );
                })}
            </>
        </ReportingDashboardSection>
    ) : null;
};

export default CheckInSection;
